import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { Profile } from '../../auth/useProfile'

/**
 * `PATCH /users/me` with `display_name` and NOTHING else. An omitted key leaves
 * the rest alone; sending `preferences` would replace the whole object, which
 * iOS also writes — so this body never carries it. `null` clears the name.
 *
 * Not optimistic: a typed field behind an explicit Save. On success the
 * server's own record goes into the cache (so the avatar re-renders at once),
 * then the key is invalidated so the cache converges. Rejects on failure so the
 * screen can show it inline and keep what was typed.
 */
export function useSetDisplayName() {
  const client = useQueryClient()
  return useMutation<Profile, Error, string | null>({
    // Offline the PATCH is attempted and rejects at once, so the screen shows
    // its inline error. The default would PAUSE it on "Saving…" indefinitely.
    // Same as useAutosaveField.
    networkMode: 'always',
    retry: false,
    mutationFn: async (displayName) => {
      const { data, error } = await api.PATCH('/api/v1/users/me', {
        body: { display_name: displayName },
      })
      if (error !== undefined || data === undefined) throw new Error('Could not save the name.')
      return data
    },
    onSuccess: (profile) => {
      client.setQueryData<Profile>(keys.user.me(), profile)
      return client.invalidateQueries({ queryKey: keys.user.me() })
    },
  })
}
